import { useState, SetStateAction, Dispatch as DispatchReact, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { IUserRole } from 'APIPackage/AuthModule/interfaces/IUserRole';
import { getPlanErknmStatusTitleByStatus } from 'APIPackage/PlanListModule/common/getPlanErknmStatusTitleByStatus';
import { IConsiderPlan } from 'APIPackage/PlanListModule/interfaces/IConsiderPlan';
import { IPlanStatusUpdate } from 'APIPackage/PlanListModule/interfaces/IPlanStatusUpdate';
import {
  getUserRole,
  getUserRoleIsProsecutorOrSuperProsecutor,
  getUserRoleIsSupervisor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { ChangePlanStatusModals } from 'CorePackage/ChangePlanStatusModalsModule/components/ChangePlanStatusModals';
import { ChangePlanStatusTypesEnum } from 'CorePackage/ChangePlanStatusModalsModule/enums/ChangePlanStatusTypesEnum';
import { IChangePlanStatusModalValues } from 'CorePackage/ChangePlanStatusModalsModule/interfaces/IChangePlanStatusModalValues';
import { TableCheckItems } from 'CorePackage/MainModule/types/TableCheckItems';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { PlanStatusEnum } from 'APIPackage/PlanListModule/enums/PlanStatusEnum';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { useKnmDocumentAttachment } from 'KnmPackage/MainModule/hooks/useKnmDocumentAttachment';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { INotification } from 'CorePackage/NotificationModule/interfaces/INotification';
import { IKnmPlanInfo } from 'APIPackage/PlanListModule/interfaces/IKnmPlanInfo';
import {
  getPlanListData,
  getPlanListStatuses,
  getPlanListTotalCount,
} from 'PlanListPackage/MainModule/selectors/planListSelectors';
import { getPlanInfo, getPlanIsPrivilegesKno } from 'PlanPackage/MainModule/selectors/planSelectors';
import { Dropdown } from 'UIPackage/MainModule/components/Dropdown';
import { IDropdownItem } from 'UIPackage/MainModule/interfaces/IDropdownItem';
import dropdownStyles from 'UIPackage/MainModule/components/Dropdown.module.scss';
import { IconMenu } from 'UIPackage/IconsModule/components/IconMenu';
import { getAccountOrganizationsData } from 'CorePackage/AccountModule/selectors/accountSelectors';
import { DropdownItem } from 'UIPackage/MainModule/components/DropdownItem';
import { changePlanStatus } from 'PlanPackage/MainModule/common/changePlanStatus';
import { Table } from 'CorePackage/TableModule/components/Table';
import { handleSortChange } from 'CorePackage/MainModule/common/handleSortChange';
import { getSortOrder } from 'CorePackage/MainModule/common/getSortOrder';
import { getSortOrderByYear } from 'CorePackage/MainModule/common/getSortOrderByYear';
import { ITableColumn } from 'CorePackage/TableModule/interfaces/ITableColumn';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IUserOrganisation } from 'APIPackage/AccountModule/interfaces/IUserOrganisation';

import {
  planListGetPlanById,
  planListUpdatePlanStatus,
  planListUpdateErknmPlanStatus,
  planListUpdatePlanStatusToConsider,
  planListSetOrder,
  planListGetDataNextPage,
} from '../actions/planListActions';
import { getErpPlanLabel } from '../common/mapPlanStatus';
import { IKnmPlan } from '../interfaces/IKnmPlan';
import { AddKnmModal } from './AddKnmModal';
import styles from './PlanListTable.module.scss';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';

interface IPlanStatusInfo {
  id: number;
  newStatus: ErknmPlanStatusesEnum;
  knmAmount: number;
}

interface ITemplatesTableProps {
  columns: ITableColumn<IKnmPlan>[];
  itemsCheck: TableCheckItems;
  searchInputValue: string;
  setItemsCheck: DispatchReact<SetStateAction<TableCheckItems>>;
  setTableColumns: DispatchReact<SetStateAction<ITableColumn<IKnmPlan>[]>>;
}

interface ITemplatesTableStoreProps {
  role: IUserRole | undefined;
  isProsecutorOrSuperProsecutor: boolean;
  isSupervisor: boolean;
  planListData: IKnmPlan[];
  planStatuses: (ErknmPlanStatusesEnum | PlanStatusEnum)[];
  isErknm: boolean;
  planInfo: IKnmPlanInfo | undefined;
  plansCount: number;
  prosecutorOrganisations: IUserOrganisation[];
  isPrivilegesKno?: boolean;
  settings: IAdminSetting[] | undefined;
}

const TemplatesTableWithoutStore = ({
  columns,
  itemsCheck,
  searchInputValue,
  setItemsCheck,
  setTableColumns,
  isErknm,
  isProsecutorOrSuperProsecutor,
  isSupervisor,
  planInfo,
  planListData,
  planStatuses,
  plansCount,
  role,
  prosecutorOrganisations,
  isPrivilegesKno,
  settings,
}: ITemplatesTableProps & ITemplatesTableStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const addNotice: (notice: INotification) => void = useCallback(
    (notice: INotification) => dispatch(addNoticeAction(notice)),
    [dispatch],
  );

  const getPlanById: (id: number) => void = useCallback((id: number) => dispatch(planListGetPlanById(id)), [dispatch]);

  const updatePlanStatus: (id: number, status: PlanStatusEnum, searchValue: string) => void = useCallback(
    (id: number, status: PlanStatusEnum, searchValue: string) =>
      dispatch(planListUpdatePlanStatus(id, status, searchValue)),
    [dispatch],
  );

  const updateErknmPlanStatus: (
    id: number,
    data: IPlanStatusUpdate,
    knmAmount: number,
    searchValue?: string,
  ) => void = useCallback(
    (id: number, data: IPlanStatusUpdate, knmAmount: number, searchValue?: string) =>
      dispatch(planListUpdateErknmPlanStatus(id, data, knmAmount, searchValue)),
    [dispatch],
  );

  const updatePlanStatusToConsider: (id: number, data: IConsiderPlan, searchValue?: string) => void = useCallback(
    (id: number, data: IConsiderPlan, searchValue?: string) =>
      dispatch(planListUpdatePlanStatusToConsider(id, data, searchValue)),
    [dispatch],
  );

  const [isAddKnmModalVisible, setIsAddKnmModalVisible] = useState(false);

  const isUploadEnabled = isErknm || prosecutorOrganisations.some(x => x.id.length === 1);
  const { downloadZipKnmAttachment, getDocumentsByPlanId } = useKnmDocumentAttachment();
  const [planStatusInfo, setPlanStatusInfo] = useState<IPlanStatusInfo | null>(null);
  const [activeModal, setActiveModal] = useState<ChangePlanStatusTypesEnum | null>(null);

  const makeAddKnmButtonClickHandler = useCallback(
    (id: number) => () => {
      getPlanById(id);
      setIsAddKnmModalVisible(true);
    },
    [getPlanById],
  );

  const handleAddKnmModalClose = useCallback(() => setIsAddKnmModalVisible(false), []);

  const handleUpdatePlanStatus = useCallback(
    (id: IKnmPlan['id'], status: PlanStatusEnum) => () => {
      updatePlanStatus(id, status, searchInputValue);
    },
    [searchInputValue, updatePlanStatus],
  );

  const makeErknmChangeStatusButtonClickHandler = useCallback(
    (plan: IKnmPlan, newStatus: ErknmPlanStatusesEnum) => () =>
      changePlanStatus({
        plan,
        planInfo,
        newStatus,
        isProsecutorOrSuperProsecutor,
        addNotice,
        setPlanStatusInfo,
        setActiveModal,
        isPrivilegesKno,
        settings,
        formRef: undefined,
      }),
    [addNotice, isPrivilegesKno, isProsecutorOrSuperProsecutor, planInfo],
  );

  const handleChangePlanStatusApply = useCallback(
    ({ comment, documents }: IChangePlanStatusModalValues) => {
      if (planStatusInfo) {
        const { id, newStatus, knmAmount } = planStatusInfo;

        updateErknmPlanStatus(
          id,
          {
            changed: null,
            oldStatus: null,
            newStatus,
            comment,
            documents,
          },
          knmAmount,
          searchInputValue,
        );
        setActiveModal(null);
      }
    },
    [planStatusInfo, searchInputValue, updateErknmPlanStatus],
  );

  const handleChangePlanStatusToConsiderApply = useCallback(
    (data: IConsiderPlan) => {
      if (planStatusInfo) {
        const { id } = planStatusInfo;

        updatePlanStatusToConsider(id, data, searchInputValue);
        setActiveModal(null);
      }
    },
    [planStatusInfo, searchInputValue, updatePlanStatusToConsider],
  );

  const handleChangePlanStatusClose = useCallback(() => setActiveModal(null), []);

  const downloadProsecutorDocs = useCallback(
    (id: IKnmPlan['id']) => async () => {
      const docs = await getDocumentsByPlanId(id);

      if (docs && docs.length) {
        const downloadDocs = docs.map(doc => {
          const { fileName, filePath, fileSize, mimeType, bucketName, signatureInfo } = doc;
          const documentPath = `${filePath}/${fileName}`;

          if (signatureInfo) {
            return doc;
          }

          return { fileName, filePath: documentPath, fileSize, mimeType, bucketName };
        });

        downloadZipKnmAttachment(downloadDocs);
      } else {
        addNotice({
          renderContent: 'Отсутствуют документы для загрузки.',
          noticeType: NoticeTypesEnum.WARNING,
          messageType: MessageTypesEnum.MISSING_PROSECUTOR_DOCS,
        });
      }
    },
    [addNotice, downloadZipKnmAttachment, getDocumentsByPlanId],
  );

  const renderErknmStatusesWindow = useCallback(
    (knmPlan: IKnmPlan) =>
      (planStatuses as ErknmPlanStatusesEnum[])
        .filter(status => {
          if (role) {
            if (isSupervisor) {
              return status !== ErknmPlanStatusesEnum.CONSIDERED;
            }
            if (isProsecutorOrSuperProsecutor) {
              return status !== ErknmPlanStatusesEnum.APPROVED;
            }
          }

          return true;
        })
        .map(status => {
          const shouldDisable = knmPlan.availableStatuses.some(el => el === status);
          const label = getPlanErknmStatusTitleByStatus(status);

          return {
            id: `status-change-${label}`,
            content: (
              <DropdownItem
                key={status}
                onClick={makeErknmChangeStatusButtonClickHandler(knmPlan, status)}
                isDisabled={!shouldDisable}
              >
                {label}
              </DropdownItem>
            ),
          };
        }),
    [isProsecutorOrSuperProsecutor, isSupervisor, makeErknmChangeStatusButtonClickHandler, planStatuses, role],
  );

  const renderStatusesWindow = useCallback(
    (knmPlan: IKnmPlan) =>
      (planStatuses as PlanStatusEnum[]).map(status => {
        const shouldDisable = knmPlan.availableStatuses.some(el => el === status);
        const label = getErpPlanLabel(status);

        return {
          id: `status-change-${label}`,
          content: (
            <DropdownItem key={status} onClick={handleUpdatePlanStatus(knmPlan.id, status)} isDisabled={!shouldDisable}>
              {label}
            </DropdownItem>
          ),
        };
      }),
    [handleUpdatePlanStatus, planStatuses],
  );

  const renderDropdown = useCallback(
    (knmPlan: IKnmPlan) => {
      if ((isErknm && !isUploadEnabled) || (!isErknm && !isProsecutorOrSuperProsecutor)) {
        return null;
      }
      const addExistingKnmItem = {
        id: 'add-existing-knm',
        content: (
          <DropdownItem onClick={makeAddKnmButtonClickHandler(knmPlan.id)}>Добавить существующее КНМ</DropdownItem>
        ),
      };
      const statusChangeItem = {
        id: 'status-change',
        content: <div className={dropdownStyles.MenuDropdownButton}>Изменить статус</div>,
        additionalContent: isErknm ? renderErknmStatusesWindow(knmPlan) : renderStatusesWindow(knmPlan),
      };
      const prosecutorDocsItem = {
        id: 'get-documents',
        content: (
          <DropdownItem onClick={downloadProsecutorDocs(knmPlan.id)}>Комплект документов для прокуратуры</DropdownItem>
        ),
      };
      const dropdownItems: IDropdownItem[] = [];

      if (!isErknm) {
        dropdownItems.push(addExistingKnmItem);
        dropdownItems.push(statusChangeItem);
      }

      if (isErknm) {
        dropdownItems.push(prosecutorDocsItem);
      }

      return (
        <Dropdown items={dropdownItems}>
          <button className={styles.MenuButton} type="button">
            <IconMenu className={styles.MenuButtonIcon} />
          </button>
        </Dropdown>
      );
    },
    [
      downloadProsecutorDocs,
      isErknm,
      isProsecutorOrSuperProsecutor,
      isSupervisor,
      isUploadEnabled,
      makeAddKnmButtonClickHandler,
      renderErknmStatusesWindow,
      renderStatusesWindow,
    ],
  );

  const handleSortChangeClick = useCallback(
    (id: ITableColumn<IKnmPlan>['id']) => {
      const newColumnsData = handleSortChange(columns, id);

      setTableColumns(newColumnsData);
      const order = getSortOrder(newColumnsData, getSortOrderByYear);

      dispatch(planListSetOrder(order));
    },
    [columns, dispatch, setTableColumns],
  );

  const handleLoadClick = useCallback(() => {
    dispatch(planListGetDataNextPage());
  }, [dispatch]);

  return (
    <>
      <Table<IKnmPlan>
        columns={columns}
        itemsCheck={itemsCheck}
        data={planListData}
        setItemsCheck={setItemsCheck}
        onSortChange={handleSortChangeClick}
        columnStyles={styles}
        checkedNames={['план', 'плана', 'планов']}
        renderDropdown={renderDropdown}
        handleLoadClick={handleLoadClick}
        totalCount={plansCount}
      />
      <AddKnmModal visible={isAddKnmModalVisible} isErknm={isErknm} onClose={handleAddKnmModalClose} />
      <ChangePlanStatusModals
        activeModal={activeModal}
        planStatus={planStatusInfo?.newStatus}
        onChangeApply={handleChangePlanStatusApply}
        onChangeToConsiderApply={handleChangePlanStatusToConsiderApply}
        onClose={handleChangePlanStatusClose}
      />
    </>
  );
};

const mapStateToProps = (state: IRootState): ITemplatesTableStoreProps => ({
  role: getUserRole(state),
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  planListData: getPlanListData(state),
  planStatuses: getPlanListStatuses(state),
  isErknm: getLawInterface(state) === LawInterfacesEnum.FZ_248,
  planInfo: getPlanInfo(state),
  plansCount: getPlanListTotalCount(state),
  prosecutorOrganisations: getAccountOrganizationsData(state).prosecutor,
  isPrivilegesKno: getPlanIsPrivilegesKno(state),
  settings: getAdminFetchSettings(state),
});

export const TemplatesTable = connect(mapStateToProps)(TemplatesTableWithoutStore);
